import React, { useEffect, useState } from 'react';
import { sparkToast } from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';
import { sanitizeTaskName } from '../../HomePage/projectNameUtils';
import { CustomerIntentModal, useCustomerIntentModal } from '@/features/customer-intents';
import { TaskTypeControls, TaskLabelsField } from '../shared/TaskTypeControls';
import PriorityRanking from '../businessGoals/PriorityRanking';
import ConstraintsEditor from '../businessGoals/ConstraintsEditor';
import {
    collectConstraintErrors,
    constraintDuplicateMetricErrors,
    DUPLICATE_CONSTRAINT_TOAST,
} from '../businessGoals/constraintOptions';
import FallbackLabelSelect from '../businessGoals/FallbackLabelSelect';
import AutonomyLevelSelect from '../businessGoals/AutonomyLevelSelect';
import { BEST_GUESS_VALUE, isFallbackSentinel } from '../businessGoals/fallbackLabel';
import LabelPrioritySelect from '../businessGoals/LabelPrioritySelect';
import { WizardNextButton } from './WizardStepHeading';
import {
    LABEL_PRIORITY_KIND,
    labelPriorityAppliesTo,
    reconcileLabelPriority,
} from '../businessGoals/labelPriority';
import { LABEL_CHOICE_FIELD } from '../businessGoals/labelsCatalogChange';

/**
 * Wizard Step 1 — "Business Goals" (Figma 3047-4189 / 3099-9289 / 3106-9825).
 *
 * Collects task name/description, task type controls, expected labels
 * (classification & NER only), drag-ranked business-goal priorities and
 * constraint rows. "Next" hands the assembled form to the wizard page,
 * which creates the usecase.
 */
/**
 * Indicative toast copy for a failing "Next" validation (Figma 3423-15453).
 * A single failing field gets a specific message; several failing fields get
 * one generic message instead of arbitrarily naming just the first field.
 */
const validationToast = (validationErrors, isNerTask) => {
    if (Object.keys(validationErrors).length > 1) {
        return {
            title: 'Missing required fields',
            content: 'Please complete the highlighted fields to continue',
        };
    }
    if (validationErrors.taskName) {
        return { title: 'Task name required', content: 'The task name cannot be empty' };
    }
    if (validationErrors.taskType) {
        return { title: 'Task type required', content: validationErrors.taskType };
    }
    if (validationErrors.classificationType) {
        return { title: 'Classification type required', content: validationErrors.classificationType };
    }
    if (validationErrors.labels) {
        return {
            title: isNerTask ? 'Entity types required' : 'Labels required',
            content: validationErrors.labels,
        };
    }
    if (validationErrors.labelPriority) {
        return { title: 'Label priority required', content: validationErrors.labelPriority };
    }
    if (validationErrors.fallbackLabel) {
        return { title: 'Fallback label required', content: validationErrors.fallbackLabel };
    }
    if (validationErrors.autonomyLevel) {
        return { title: 'Autonomy level required', content: validationErrors.autonomyLevel };
    }
    return { title: 'Invalid constraints', content: 'Please fix the highlighted constraint fields' };
};

const BusinessGoalsStep = ({
    form,
    onFormChange,
    onNext,
    isSubmitting = false,
    demoLoading = false,
    submitError = null,
    /**
     * `LABEL_CHOICE_FIELD` select to scroll to and open on arrival (Data
     * Collection's "Adjust Fallback" / "Adjust Label Priorities"); the page
     * clears it through `onFocusFieldHandled` once consumed.
     */
    focusField = null,
    onFocusFieldHandled,
}) => {
    const [errors, setErrors] = useState({});
    const customerIntent = useCustomerIntentModal();

    useEffect(() => {
        if (!focusField) return;
        document
            .querySelector(`[data-walkthrough="${focusField}"]`)
            ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        onFocusFieldHandled?.();
    }, [focusField, onFocusFieldHandled]);

    const {
        taskName,
        description,
        taskType,
        classificationType,
        labels,
        fallbackLabel,
        fallbackLabelUserSet,
        labelPriority,
        autonomyLevel = null,
        priorityOrder,
        constraints,
    } = form;

    const update = (patch) => {
        const nextPatch = { ...patch };
        // Editing labels invalidates a selected fallback label that no longer
        // exists (Best guess / No predictions always stay valid).
        if (
            Array.isArray(nextPatch.labels) &&
            fallbackLabel &&
            !isFallbackSentinel(fallbackLabel) &&
            !nextPatch.labels.includes(fallbackLabel)
        ) {
            nextPatch.fallbackLabel = null;
            // The explicit choice is gone — the select is auto-managed again.
            nextPatch.fallbackLabelUserSet = false;
        }
        // "What matters most" only applies to single-label classification with
        // exactly two labels. Reconcile it with the type / labels this patch
        // resolves to: drop it (and the fallback it derived) once the section
        // stops applying or its label is gone; otherwise keep the fallback in
        // lockstep — options 2–5 pin the fallback to the other label, while
        // "equally important" leaves it to the visible Fallback select.
        const nextTaskType = nextPatch.taskType ?? taskType;
        const nextClassificationType = nextPatch.classificationType ?? classificationType;
        Object.assign(
            nextPatch,
            reconcileLabelPriority({
                taskType: nextTaskType,
                classificationType: nextClassificationType,
                labels: nextPatch.labels ?? labels,
                labelPriority: 'labelPriority' in nextPatch ? nextPatch.labelPriority : labelPriority,
                fallbackLabelUserSet:
                    'fallbackLabelUserSet' in nextPatch
                        ? nextPatch.fallbackLabelUserSet
                        : fallbackLabelUserSet,
            })
        );
        // When no fallback label is selected yet and a label is some variation
        // of "other" (Other/OTHER/…), default the fallback to it — the natural
        // catch-all. The user can still pick a different one.
        if (
            Array.isArray(nextPatch.labels) &&
            nextTaskType === TaskType.classification &&
            !(nextPatch.fallbackLabel ?? fallbackLabel)
        ) {
            const otherLabel = nextPatch.labels.find((l) => l.toLowerCase() === 'other');
            if (otherLabel) nextPatch.fallbackLabel = otherLabel;
        }
        // Best guess is single-label only; switching to multi clears it.
        const nextFallback =
            'fallbackLabel' in nextPatch ? nextPatch.fallbackLabel : fallbackLabel;
        if (nextClassificationType === 'multi' && nextFallback === BEST_GUESS_VALUE) {
            nextPatch.fallbackLabel = null;
            nextPatch.fallbackLabelUserSet = false;
        }
        // The autonomy level is picked for a task type; switching the type
        // sends it back to the form default (unselected) so a level chosen
        // for the previous type does not carry over unnoticed.
        if ('taskType' in nextPatch && nextPatch.taskType !== taskType) {
            nextPatch.autonomyLevel = null;
        }
        onFormChange({ ...form, ...nextPatch });
        // Clear a field's error as soon as the user addresses it.
        setErrors((prev) => {
            const next = { ...prev };
            if (patch.classificationType) delete next.classificationType;
            if (patch.labels?.length) delete next.labels;
            if (patch.fallbackLabel) delete next.fallbackLabel;
            if (patch.labelPriority) {
                delete next.labelPriority;
                // Options 2–5 derive (and hide) the fallback; "equally
                // important" re-shows it, so its error is re-evaluated on Next.
                delete next.fallbackLabel;
            }
            if (patch.autonomyLevel) delete next.autonomyLevel;
            if (patch.taskType) {
                delete next.taskType;
                delete next.classificationType;
                delete next.labels;
                delete next.fallbackLabel;
                delete next.labelPriority;
                delete next.autonomyLevel;
            }
            // Re-validate only rows that already show an error, so fixing (or
            // removing) a row clears its message without flagging new rows.
            if (patch.constraints && next.constraints) {
                const stillInvalid = {};
                const constraintErrors = collectConstraintErrors(patch.constraints);
                patch.constraints.forEach((c) => {
                    if (next.constraints[c.id] && constraintErrors[c.id]) {
                        stillInvalid[c.id] = constraintErrors[c.id];
                    }
                });
                if (Object.keys(stillInvalid).length) next.constraints = stillInvalid;
                else delete next.constraints;
            }
            return next;
        });
    };

    const isClassification = taskType === TaskType.classification;
    const isNer = taskType === TaskType.NER;
    // Classification reveals the labels section only once single/multi is
    // chosen; NER has no such sub-choice so it shows right away.
    const showLabelsSection = isNer || (isClassification && Boolean(classificationType));
    // Single-label classification with exactly two labels asks "What matters
    // most for these labels?" instead of the fallback label: options 2–5
    // derive the fallback (the other label) and seed the Train defaults.
    const showLabelPrioritySection =
        isClassification && labelPriorityAppliesTo({ classificationType, labels });
    // Fallback label applies to both classification types and is required
    // whenever it is shown. The select starts empty so the user must pick
    // No predictions or a label (single-label also offers Best guess).
    // It only appears once at least one label exists to choose from — and,
    // while the priority select is shown, only for "equally important",
    // where the user still picks the fallback explicitly.
    const showFallbackSection =
        isClassification &&
        labels.length > 0 &&
        Boolean(classificationType) &&
        (!showLabelPrioritySection || labelPriority?.kind === LABEL_PRIORITY_KIND.equal);

    const handleNext = () => {
        const nextErrors = {};
        if (!taskName.trim()) nextErrors.taskName = 'Task name is required';
        if (!taskType) nextErrors.taskType = 'Please select a task type';
        if (isClassification && !classificationType) {
            nextErrors.classificationType = 'Please select Yes or No';
        }
        if (showLabelsSection && !labels.length) {
            nextErrors.labels = isNer
                ? 'Please add at least one entity type'
                : 'Please add at least one label';
        }
        if (showLabelPrioritySection && !labelPriority) {
            nextErrors.labelPriority = 'Please select what matters most for these labels';
        }
        if (showFallbackSection && !fallbackLabel) {
            nextErrors.fallbackLabel = 'Please select a fallback label';
        }
        if (!autonomyLevel) nextErrors.autonomyLevel = 'Please select an autonomy level';
        const constraintErrors = collectConstraintErrors(constraints);
        if (Object.keys(constraintErrors).length) nextErrors.constraints = constraintErrors;
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) {
            const onlyConstraintErrors =
                Object.keys(nextErrors).length === 1 && nextErrors.constraints;
            const hasDuplicate =
                onlyConstraintErrors &&
                Object.keys(constraintDuplicateMetricErrors(constraints)).length > 0;
            const { title, content } = hasDuplicate
                ? DUPLICATE_CONSTRAINT_TOAST
                : validationToast(nextErrors, isNer);
            sparkToast.error(title, { content, detailed: true, duration: 5000 });
            return;
        }
        onNext();
    };

    // Validation errors take precedence over the server-side create error
    // (e.g. "task already exists"), which is cleared once the name is edited.
    const taskNameError = errors.taskName || submitError;

    return (
        <div className="cw-step cw-step--business-goals fs-unmask">
            <div className="cw-title-row">
                <div className="cw-title-row__fields">
                    <input
                        className="cw-task-name-input"
                        data-walkthrough="task-name"
                        placeholder="Task name"
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- entering the wizard starts with naming the task
                        autoFocus
                        value={taskName}
                        onChange={(e) => {
                            update({ taskName: sanitizeTaskName(e.target.value) });
                            if (errors.taskName) {
                                setErrors((prev) => {
                                    const { taskName: _removed, ...rest } = prev;
                                    return rest;
                                });
                            }
                        }}
                        aria-label="Task name"
                    />
                    {taskNameError && <div className="cw-field-error">{taskNameError}</div>}
                    <input
                        className="cw-task-description-input"
                        placeholder="Add task description"
                        value={description}
                        onChange={(e) => update({ description: e.target.value })}
                        aria-label="Task description"
                    />
                </div>
            </div>

            <TaskTypeControls
                taskType={taskType}
                classificationType={classificationType}
                onPatch={update}
                explainerText="Depending on the task, you'll get one specific result: category tags (for classification), specific words pulled from the text (for entity extraction), a quick written summary (for summarization), or a free-form written response (for content generation)."
                taskTypeError={errors.taskType}
                classificationTypeError={errors.classificationType}
                onContactUs={customerIntent.openModal}
            />

            {showLabelsSection && (
                <div className="cw-labels-fallback-row">
                    <TaskLabelsField
                        taskType={taskType}
                        labels={labels}
                        onPatch={update}
                        error={errors.labels}
                    />
                    {showLabelPrioritySection && (
                        <LabelPrioritySelect
                            labels={labels}
                            value={labelPriority}
                            onChange={(choice) => update({ labelPriority: choice })}
                            error={errors.labelPriority}
                            autoOpen={focusField === LABEL_CHOICE_FIELD.priority}
                        />
                    )}
                    {showFallbackSection && (
                        <FallbackLabelSelect
                            labels={labels}
                            classificationType={classificationType}
                            value={fallbackLabel}
                            onChange={(next) =>
                                update({ fallbackLabel: next, fallbackLabelUserSet: true })
                            }
                            error={errors.fallbackLabel}
                            autoOpen={focusField === LABEL_CHOICE_FIELD.fallback}
                        />
                    )}
                </div>
            )}

            <AutonomyLevelSelect
                value={autonomyLevel}
                onChange={(next) => update({ autonomyLevel: next })}
                error={errors.autonomyLevel}
                onContactUs={customerIntent.openModal}
            />

            <div className="cw-goals-section">
                <h3 className="cw-goals-section__title">Define &amp; Prioritize Business Goals</h3>
                <p className="cw-goals-section__subtitle">
                    Drag and drop to rank your priorities. specific.ai will optimize the model based
                    on these trade-offs.
                </p>
                <PriorityRanking
                    order={priorityOrder}
                    onChange={(next) => update({ priorityOrder: next })}
                />
            </div>

            <ConstraintsEditor
                constraints={constraints}
                onChange={(next) => update({ constraints: next })}
                isNerTask={isNer}
                entityTypeOptions={labels}
                errors={errors.constraints}
            />

            {/* Rendered last so Tab reaches "Next" only after every other field
                in the step; the step grid places it back into the title row's
                visual slot (top-right) and it sticks in lockstep with the row. */}
            <div className="cw-title-row__next">
                <WizardNextButton
                    onClick={handleNext}
                    loading={isSubmitting}
                    loadingLabel="Saving…"
                    demoLoading={demoLoading}
                    disableWhenReadOnly
                />
            </div>

            <CustomerIntentModal
                open={customerIntent.open}
                onClose={customerIntent.closeModal}
                initialSelection={customerIntent.initialSelection}
            />
        </div>
    );
};

export default BusinessGoalsStep;
